import BatteryChargingFullIcon from "@mui/icons-material/BatteryChargingFull";
import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";
import useTheme from "@mui/material/styles/useTheme";
import {
  AssetLabelBadge,
  BADGE_GAP_PX,
  BADGE_ICON_PX,
  BADGE_TEXT_PADDING_PX,
  badgePercentText,
} from "../models/assetLabelBadge";
import {
  AssetLabel,
  LABEL_FONT_FAMILY,
  LABEL_FONT_SIZE_PX,
  LABEL_LINE_HEIGHT,
  LABEL_PADDING_X,
  LABEL_PADDING_Y,
  nearestPointOnRect,
} from "./assetLabelLayout";
import { DRIVE_TYPE_ICONS } from "./driveTypeIcon";

/** Below this the name is sitting on its vehicle and a line between them would be a smudge. */
const MIN_CONNECTOR_PX = 4;

/**
 * One name box.
 *
 * `styled` rather than `sx`, and the geometry through `style` rather than a class: the labels are
 * laid out again on every animation frame, and building a fresh emotion style for each of a few
 * hundred boxes sixty times a second is the expensive part of drawing them.
 *
 * The box is exactly the rectangle the layout reserved — same font, same padding — so a name sits
 * centred over its vehicle and the connecting line ends where the box really is.
 */
const LabelChip = styled("div")(({ theme }) => ({
  position: "absolute",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: BADGE_GAP_PX,
  color: theme.palette.text.primary,
  backgroundColor: theme.palette.background.paper,
  boxSizing: "border-box",
  padding: `${LABEL_PADDING_Y}px ${LABEL_PADDING_X}px`,
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[1],
  fontFamily: LABEL_FONT_FAMILY,
  fontSize: LABEL_FONT_SIZE_PX,
  lineHeight: `${LABEL_LINE_HEIGHT}px`,
  whiteSpace: "nowrap",
  overflow: "hidden",
}));

/**
 * The short word for how a vehicle is powered, before its name.
 *
 * Its own component for the same reason as the other two: the overlay is redrawn on every frame
 * while anything moves, and this changes only when the status poll comes back.
 */
const PowertrainChip = styled("span")(({ theme }) => ({
  padding: `0 ${BADGE_TEXT_PADDING_PX}px`,
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
  fontSize: LABEL_FONT_SIZE_PX - 2,
  lineHeight: `${LABEL_LINE_HEIGHT - 2}px`,
  flexShrink: 0,
}));

/** The name itself, which is what gives way if the box turns out too narrow. */
const LabelName = styled("span")({
  overflow: "hidden",
  textOverflow: "ellipsis",
});

/**
 * The charging bolt that goes before a name.
 *
 * Its own component, like {@link LabelLevel}, because the whole overlay is re-rendered on every
 * animation frame while any one vehicle moves, while a badge only changes when the status poll
 * comes back. At a component boundary that work can be skipped; inside the surrounding `map` it
 * could not be.
 */
function LabelCharging() {
  return <BatteryChargingFullIcon color="primary" sx={{ fontSize: BADGE_ICON_PX }} />;
}

/** How a vehicle is powered and how full it is, after its name. */
function LabelLevel({ badge }: { badge: AssetLabelBadge }) {
  const DriveTypeIcon = DRIVE_TYPE_ICONS[badge.driveType];

  return (
    <>
      <DriveTypeIcon sx={{ fontSize: BADGE_ICON_PX }} />
      <span>{badgePercentText(badge)}</span>
    </>
  );
}

interface Props {
  labels: AssetLabel[];
  /** Extra facts to show beside a name, by device id. Without an entry only the name is drawn. */
  badgeById?: ReadonlyMap<string, AssetLabelBadge>;
  /** The reader's own colours. Left out, the theme's are used. */
  color?: string;
  backgroundColor?: string;
}

/**
 * The vehicle names, drawn over the map.
 *
 * Not a deck.gl `TextLayer`: the names have to avoid each other, which means laying them out in
 * screen space and then drawing them where the layout put them — see
 * {@link buildAssetLabelLayout}. A name that had to move away from its vehicle gets a thin line
 * back to it, without which a crowded depot becomes a set of names belonging to nobody.
 *
 * The whole overlay ignores the pointer, so it never gets in the way of clicking a vehicle.
 */
export function AssetLabelOverlay({ labels, badgeById, color, backgroundColor }: Props) {
  const theme = useTheme();
  const textColor = color || theme.palette.text.primary;
  const boxColor = backgroundColor || theme.palette.background.paper;

  if (labels.length === 0) {
    return null;
  }

  return (
    <Box sx={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 800 }}>
      <Box
        component="svg"
        aria-hidden="true"
        sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", overflow: "visible" }}
      >
        {labels.map(label => {
          const [endX, endY] = nearestPointOnRect(label, label.anchorX, label.anchorY);
          if (Math.hypot(endX - label.anchorX, endY - label.anchorY) < MIN_CONNECTOR_PX) {
            return null;
          }

          return (
            <line
              key={label.id}
              x1={label.anchorX}
              y1={label.anchorY}
              x2={endX}
              y2={endY}
              stroke={textColor}
              strokeWidth={1}
              strokeOpacity={0.55}
              strokeDasharray="3 2"
            />
          );
        })}
      </Box>

      {labels.map(label => {
        const badge = badgeById?.get(label.id);

        return (
          <LabelChip
            key={label.id}
            style={{
              left: label.x,
              top: label.y,
              width: label.width,
              height: label.height,
              color: textColor,
              backgroundColor: boxColor,
            }}
          >
            {badge?.powertrainLabel ? (
              <PowertrainChip>{badge.powertrainLabel}</PowertrainChip>
            ) : null}
            {badge?.isCharging ? <LabelCharging /> : null}
            <LabelName>{label.text}</LabelName>
            {badge?.energyPercent !== undefined ? <LabelLevel badge={badge} /> : null}
          </LabelChip>
        );
      })}
    </Box>
  );
}
